import { useForm } from "react-hook-form";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import React, { Fragment, useEffect, useRef } from "react";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import RadioGroupComponent from "@/app/components/ui/RadioGroup";
import { ICartState } from "@/app_redux/reducers/slices/auth/cart_slice";
import { IRefundrejectApprove } from "@/api_framework/api_modals/payment";
import { usePaymentRefundRequestListAPI } from "@/app/hooks/api_hooks/payment/usePaymentRefundRequestListAPI";
import { usePaymentUserRefundApproveAndRejectAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserRefundApproveAndRejectAPI";
import { usePaymentRefundRequestApprovalDetailsAPI } from "@/app/hooks/api_hooks/payment/usePaymentRefundRequestApprovalDetailsAPI";

type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  invoiceId: string;
  approvalStatus: string;
  handelClickToFetch: () => void;
};

const RefundRejectModalComponent: React.FC<Props> = ({ open, setOpen, invoiceId, approvalStatus }) => {
  const { getRouteKey } = useRouter();
  const cancelButtonRef = useRef(null);
  const { refundRequestFilter } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState.state as ICartState;
  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);
  const { execute: refundApproveAndRejected } = usePaymentUserRefundApproveAndRejectAPI();
  const { execute: fetchRequestedApproval } = usePaymentRefundRequestListAPI();
  const { execute: fetchRefundRequestedData, refundApprovalData } = usePaymentRefundRequestApprovalDetailsAPI();

  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {
      refund_type: "wallet",
    },
  });

  useEffect(() => {
    if (refundRequestFilter) {
      formHook.setValue("filters", refundRequestFilter);
    }
  }, [refundRequestFilter]);

  const handelSubmit = () => {
    const constructedBody: IRefundrejectApprove = {
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      user_id: refundApprovalData?.user_id,
      refund_request_id: refundApprovalData?.refund_request_id,
      refund_type: formHook.getValues("refund_type"),
      request_status: approvalStatus === "Approve" ? "approved" : "rejected",
    };
    refundApproveAndRejected(constructedBody).then(({ status }) => {
      if (status) {
        setOpen(false);
        const filter = formHook.getValues("filters");
        fetchRequestedApproval(filter?.startdate, filter?.enddate, filter?.user ? filter?.user : "", filter?.status);
      }
    });
  };

  useEffect(() => {
    if (invoiceId) {
      fetchRefundRequestedData(invoiceId);
    }
  }, [invoiceId]);

  const calculateTotalReturn = () => {
    const totalReturn = refundApprovalData?.refund_items?.reduce((total, item) => {
      return total + (item.refund_request_amount || 0);
    }, 0);
    return totalReturn;
  };

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => {
            setOpen(false);
          }}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-5xl">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-center mt-2">
                    <h2 className="text-sm font-medium">
                      Are you sure you want to {approvalStatus === "Approve" ? "Approve" : "Reject"} Request?
                    </h2>
                    <span
                      onClick={() => {
                        setOpen(false);
                      }}
                    >
                      <XMarkIcon className="h-4 w-4 cursor-pointer" />
                    </span>
                  </div>
                  <div className={`bg-white py-1 rounded-lg overflow-scroll mt-4`}>
                    <table className="rounded-md divide-y divide-gray-200 min-w-full">
                      <thead className="top-0 sticky bg-[#F7F7F7] shadow-sm rounded-md z-10">
                        <tr>
                          {tableHeader.map((key, index) => (
                            <th
                              key={index}
                              scope="col"
                              className={`px-3 py-2 text-left text-xs font-medium text-[#000000] capitalize tracking-wider ${key.item === "Action" && "border-l"}`}
                            >
                              {key.item}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {refundApprovalData?.refund_items?.map((cl, index) => (
                          <tr key={index} className="border-b">
                            <td className={`px-3 py-4 whitespace-nowrap text-xs font-normal`}>
                              <span className="text-xs">{cl?.name} &nbsp; </span>
                            </td>
                            <td className={`px-3 py-4 whitespace-nowrap text-xs font-normal text-gray-900`}>
                              {cl?.qty}
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-xs font-normal text-gray-900">
                              {cl?.unit_price}
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-xs font-normal text-gray-900">
                              {cl?.net_price}
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-xs font-normal text-gray-900">
                              {cl?.refund_request_amount}
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-xs font-normal text-gray-900">
                              {cl?.reason || "NA"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {approvalStatus === "Approve" && (
                      <>
                        <div className="bg-[#F2F2F2] w-full p-3 mt-3 mb-1">
                          <p className="text-[#141718] font-semibold text-xs">
                            Total Refund Amount: ₹{calculateTotalReturn()}{" "}
                          </p>
                        </div>
                        <div className="flex items-center gap-20 border-t border-b py-4">
                          <p>Refund Credit</p>
                          <RadioGroupComponent
                            alignment="horizontal"
                            errorMessages={[]}
                            fieldError={formHook.formState.errors.refund_type}
                            register={formHook.register("refund_type")}
                            groupOptions={[
                              { id: "wallet", title: "Wallet" },
                              { id: "back_to_source", title: "Back to source" },
                            ]}
                            defaultSelectedId={formHook.watch("refund_type")}
                            onRadioChange={(optionId: string) => {
                              formHook.setValue("refund_type", optionId as any);
                            }}
                            radioOptionClassName="text-sm"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex justify-end gap-4 mt-4">
                    <button
                      type="submit"
                      onClick={() => {
                        setOpen(false);
                      }}
                      className="rounded-md bg-[#72767f1a] px-4 py-2 text-xs text-[#000000] font-medium "
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="rounded-md bg-[#193A6A] font-bold px-4 py-2 text-xs text-white "
                      onClick={() => {
                        handelSubmit();
                      }}
                    >
                      Yes, {approvalStatus}
                    </button>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default RefundRejectModalComponent;

const tableHeader = [
  { item: "Item" },
  { item: "Ordered Qty" },
  { item: "Unit Price" },
  { item: "Net Price" },
  { item: "Refund Amount" },
  { item: "Reason for Refund" },
];
